import { useState } from 'react';
import { Box, Drawer, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import copy from 'copy-to-clipboard';
import { IoClose } from 'react-icons/io5';
import { MdCheck, MdClose, MdContentCopy } from 'react-icons/md';
import dayjs from 'dayjs';
import { useGetWebhookLog } from '@/apis/agency/webhooks/hook';
import { WebhookLog } from '@/apis/agency/webhooks/types';
import { BuilderEmptyStateView } from '@/components/Common/BuilderEmptyStateView';

function isSuccessStatus(log: WebhookLog) {
  if (log.http_status != null) return log.http_status >= 200 && log.http_status < 300;
  return log.status === 'success' || log.status === 'processed';
}

function StatusBadge({ log }: { log: WebhookLog }) {
  const success = isSuccessStatus(log);
  const code = log.http_status ?? (success ? 'OK' : 'ERR');

  return (
    <Box
      display='inline-flex'
      alignItems='center'
      gap={0.5}
      px={1}
      py={0.35}
      borderRadius='6px'
      sx={{
        bgcolor: success ? 'success.light' : 'error.light',
        color: success ? 'success.main' : 'error.main',
        flexShrink: 0
      }}
    >
      {success ? <MdCheck size={14} /> : <MdClose size={14} />}
      <Typography fontSize={12} fontWeight={600} lineHeight={1.2}>
        {code}
      </Typography>
    </Box>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  if (!text || text === '—') return null;

  return (
    <Tooltip title={copied ? 'Copied' : 'Copy'} open={copied || undefined} onClose={() => setCopied(false)}>
      <Box
        component='button'
        type='button'
        onClick={() => {
          copy(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        aria-label='Copy'
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          p: 0,
          m: 0,
          color: '#777777',
          fontSize: 13,
          fontWeight: 400,
          fontFamily: 'inherit',
          lineHeight: 1,
          '&:hover': { color: 'text.primary' }
        }}
      >
        <MdContentCopy size={15} />
        Copy
      </Box>
    </Tooltip>
  );
}

function SectionHeader({ label, copyText }: { label: string; copyText?: string }) {
  return (
    <Box display='flex' alignItems='center' justifyContent='space-between' mb={1} minHeight={20}>
      <Typography fontSize={11} fontWeight={700} color='text.primary' letterSpacing={0.6} textTransform='uppercase'>
        {label}
      </Typography>
      {copyText ? <CopyButton text={copyText} /> : null}
    </Box>
  );
}

function formatHeaderValue(value: unknown) {
  if (value == null) return '—';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.join(', ');
  return JSON.stringify(value);
}

function headersToText(data: Record<string, unknown> | null | undefined) {
  const entries = Object.entries(data || {});
  if (!entries.length) return '';
  return entries.map(([key, value]) => `${key}: ${formatHeaderValue(value)}`).join('\n');
}

function valueToText(value: unknown) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && Object.keys(value as object).length === 0) return '';
  return JSON.stringify(value, null, 2);
}

function ContentBox({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        bgcolor: '#F7F8FA',
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        px: 2,
        py: 2,
        opacity: 0.5,
        color: '#242424'
      }}
    >
      {children}
    </Box>
  );
}

function HeadersContent({ data }: { data: Record<string, unknown> | null | undefined }) {
  const text = headersToText(data);

  if (!text) {
    return <BuilderEmptyStateView message='No data present' />;
  }

  return (
    <Box
      component='pre'
      sx={{
        m: 0,
        p: 0,
        bgcolor: 'transparent',
        fontSize: 12,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',

        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        color: '#242424',
        lineHeight: 1.55
      }}
    >
      {text}
    </Box>
  );
}

function JsonContent({ value }: { value: unknown }) {
  const text = valueToText(value);

  if (!text) {
    return <BuilderEmptyStateView message='No data present' />;
  }

  return (
    <Box
      component='pre'
      sx={{
        m: 0,
        p: 0,
        bgcolor: 'transparent',
        fontSize: 13,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',

        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        color: '#242424',
        lineHeight: 1.55
      }}
    >
      {text}
    </Box>
  );
}

export default function WebhookLogDetail() {
  const navigate = useNavigate();
  const { data: log, isLoading } = useGetWebhookLog();

  const metaParts = log
    ? [
        log.webhook?.name || '—',
        `Attempt ${log.attempt ?? '—'}`,
        log.response_time_ms != null ? `${log.response_time_ms}ms` : null,
        dayjs(log.created_at).format('MM/DD/YYYY, HH:mm')
      ].filter(Boolean)
    : [];

  const endpointText = log?.endpoint_url ? `POST ${log.endpoint_url}` : '';
  const requestHeadersText = headersToText(log?.request_headers);
  const requestBodyText = valueToText(log?.request_body);
  const responseHeadersText = headersToText(log?.response_headers);
  const responseBodyText = valueToText(log?.response_body);

  return (
    <Drawer
      anchor='right'
      open
      onClose={() => navigate(-1)}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 480, md: 560 },
          p: 0,
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {isLoading || !log ? (
        <Box display='flex' alignItems='center' justifyContent='space-between' px={3} py={3}>
          <Typography color='text.secondary'>Loading…</Typography>
          <IconButton onClick={() => navigate(-1)}>
            <IoClose />
          </IconButton>
        </Box>
      ) : (
        <>
          <Box
            px={3}
            pt={3}
            pb={2}
            sx={{
              borderBottom: '1px solid #EAEAEA',
              flexShrink: 0
            }}
          >
            <Box display='flex' alignItems='flex-start' justifyContent='space-between' gap={1}>
              <Box display='flex' alignItems='center' gap={1} flexWrap='wrap'>
                <StatusBadge log={log} />
                <Typography fontSize={18} fontWeight={500} color='text.primary'>
                  {log.event_type || '—'}
                </Typography>
              </Box>
              <IconButton onClick={() => navigate(-1)} sx={{ mt: -0.5 }}>
                <IoClose />
              </IconButton>
            </Box>
            <Typography fontSize={13} color='text.secondary'>
              {metaParts.join(' · ')}
            </Typography>
          </Box>

          <Stack gap={2.5} px={3} py={2.5} flex={1} overflow='auto'>
            <Box>
              <SectionHeader label='Endpoint' copyText={endpointText} />
              <ContentBox>
                <Typography
                  fontSize={13}
                  sx={{
                    wordBreak: 'break-all',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace'
                  }}
                  color='text.primary'
                >
                  {endpointText || '—'}
                </Typography>
              </ContentBox>
            </Box>

            <Box>
              <SectionHeader label='Request Headers' copyText={requestHeadersText} />
              <ContentBox>
                <HeadersContent data={log.request_headers} />
              </ContentBox>
            </Box>

            <Box>
              <SectionHeader label='Request Body' copyText={requestBodyText} />
              <ContentBox>
                <JsonContent value={log.request_body} />
              </ContentBox>
            </Box>

            <Box>
              <SectionHeader label='Response Headers' copyText={responseHeadersText} />
              <ContentBox>
                <HeadersContent data={log.response_headers} />
              </ContentBox>
            </Box>

            <Box>
              <SectionHeader label='Response Body' copyText={responseBodyText} />
              <ContentBox>
                <JsonContent value={log.response_body} />
              </ContentBox>
            </Box>

            {log.error_message && (
              <Box>
                <SectionHeader label='Error' copyText={log.error_message} />
                <ContentBox>
                  <Typography fontSize={13} color='error.main'>
                    {log.error_message}
                  </Typography>
                </ContentBox>
              </Box>
            )}
          </Stack>
        </>
      )}
    </Drawer>
  );
}
